import { useCallback } from 'react';
import type { SpaceWire } from 'types/space';
import type { SpaceAction } from '../types';
import { useSpaceBus } from './SpaceBusProvider';
import { resolveWireTargets } from './resolveWireTargets';
import type { LegacyBridgeEventType } from './legacyEventTypes';

export interface UseSpaceWiresOptions {
  wires?: SpaceWire[];
  /** Forwarded first, unconditionally — existing `onAction` consumers (inspect/composer hooks) are unaffected by wiring. */
  onAction?: (action: SpaceAction) => void;
  /** Host-injected bridge into the legacy global event bus; omit if the host has none (e.g. tests, non-PWA hosts). */
  onLegacyBridge?: (eventType: LegacyBridgeEventType, action: SpaceAction) => void;
}

/**
 * Returns an `onAction` handler to pass to `CompositionHost`/`SpaceRendererOrganism` that, in
 * addition to calling `onAction`, routes the action through the document's declared `wires[]`:
 * delivers matched actions to their target Section over the per-instance `SpaceBusProvider`, and
 * bridges the closed legacy-event subset via `onLegacyBridge` (SC6).
 */
export function useSpaceWires({ wires, onAction, onLegacyBridge }: UseSpaceWiresOptions): (action: SpaceAction) => void {
  const bus = useSpaceBus();

  return useCallback(
    (action: SpaceAction) => {
      onAction?.(action);
      resolveWireTargets(wires, action).forEach(({ to, wireId, legacyEventType }) => {
        bus.publish(to, action, wireId);
        if (legacyEventType) {
          onLegacyBridge?.(legacyEventType, action);
        }
      });
    },
    [wires, onAction, onLegacyBridge, bus]
  );
}
